import type { JSX } from 'react';
import { isRouteErrorResponse, useParams, useRouteError } from 'react-router';
import { type ErrorResponse } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '../utils/error-helper';
import { isRateLimitData } from '../types/rate-limit';

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => JSX.Element | null;

export function GeneralErrorBoundary({
  defaultStatusHandler = ({ error }) => (
    <div className='flex flex-col gap-6'>
      <h1>Error {error.status}</h1>
      <p className='text-body-lg text-muted-foreground'>
        {typeof error.data === 'string' ? error.data : null}
      </p>
      <a href='/' className='text-body-md underline'>
        Back to home
      </a>
    </div>
  ),
  statusHandlers,
  unexpectedErrorHandler = (error) => (
    <div className='flex flex-col gap-6'>
      <h1>Something went wrong</h1>
      <p className='text-body-lg text-muted-foreground'>{getErrorMessage(error)}</p>
      <a href='/' className='text-body-md underline'>
        Back to home
      </a>
    </div>
  ),
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => JSX.Element | null;
}) {
  const error = useRouteError();
  const params = useParams();
  const { t } = useTranslation('common');

  if (typeof document !== 'undefined') {
    console.error(error);
  }

  const rateLimitHandler: StatusHandler = ({ error: rateLimitError }) => {
    const retryAfter = isRateLimitData(rateLimitError.data)
      ? rateLimitError.data.retryAfter
      : undefined;

    return (
      <div className='flex flex-col gap-6'>
        <h1>{t('errors.rate_limited_title')}</h1>
        <p className='text-body-lg text-muted-foreground'>{t('errors.rate_limited_body')}</p>
        {retryAfter !== undefined && (
          <p className='text-body-lg text-muted-foreground'>
            {t('errors.rate_limited_retry_in', { seconds: retryAfter })}
          </p>
        )}
        <a href='/' className='text-body-md underline'>
          {t('errors.back_home')}
        </a>
      </div>
    );
  };

  const handlerFor = (status: number) =>
    statusHandlers?.[status] ?? (status === 429 ? rateLimitHandler : defaultStatusHandler);

  return (
    <div className='container flex items-center justify-center p-20 text-h2'>
      {isRouteErrorResponse(error)
        ? handlerFor(error.status)({ error, params })
        : unexpectedErrorHandler(error)}
    </div>
  );
}
